import React, { useEffect, useState, useMemo } from "react";
import moment from 'moment';
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useIntl } from "react-intl";
import { Scatter } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    LinearScale,
    PointElement,
    LineElement,
    Tooltip,
    Legend
} from 'chart.js';

//Components
import {
    Container,
    Breadcrumb,
    LinearProgress,
    Pill
} from "@gpstrackit/gtc-react-controls";
import { Grid, Paper, Skeleton, Collapse } from '@mui/material';
import { Tabs, TabContainer, IconButton } from "@gpstrackit/gtc-react-controls";

ChartJS.register(LinearScale, PointElement, LineElement, Tooltip, Legend);

//Containers
import Preferences from "Core/containers/Preferences";
import OverviewTabDriver from "../overviewTabDriver";

//Actions
import { savePreference } from 'Redux/actions'
import { getDriverPerformanceDriver, getDriversSummary, getHeatmapZoneStats } from './redux/slice'
import { getRiskByDriver, getRiskEventDistribution } from 'Modules/driverPerformance/views/overviewTabDriver/redux/slice';
import { GetFormatForMoment } from 'Core/data/Helpers';
let OVERVIEW_KEY = 'driverDetail'

//Styles
import './style.scss'

const KEY_PREFERENCE = "DrpDriversTab";

const RiskMatrixChart = ({ driversSummaryData }) => {
    const { messages } = useIntl();
    const dispatch = useDispatch();
    const location = useLocation();
    const [showZoneInfo, setShowZoneInfo] = useState(false);
    const [clickPosition, setClickPosition] = useState({ x: 0, y: 0 });

    const zoneStats = useSelector(state => state.driverPerformanceDriverViewRouteRedux.zoneStats);

    useEffect(() => {
        const handleClickOutside = () => setShowZoneInfo(false);
        if (showZoneInfo) {
            document.addEventListener('click', handleClickOutside);
            return () => document.removeEventListener('click', handleClickOutside);
        }
    }, [showZoneInfo]);

    const chartDataPoints = useMemo(() => {
        if (!driversSummaryData?.drivers || driversSummaryData.drivers.length === 0) {
            return [];
        }

        return driversSummaryData.drivers.map(driver => ({
            x: driver.normalizedAvgEvents || 0,
            y: driver.avgSeverity || 0,
            driverName: driver.driverName || 'N/A',
            riskScore: driver.riskScore || 0,
            avgSeverity: driver.avgSeverity || 0,
            normalizedAvgEvents: driver.normalizedAvgEvents || 0
        }));
    }, [driversSummaryData]);

    const chartData = {
        datasets: [
            {
                label: 'Risk Level',
                data: chartDataPoints,
                backgroundColor: '#000000',
                pointRadius: 3,
                pointHoverRadius: 10,
            }
        ]
    };

    const gradientBackgroundPlugin = {
        id: 'gradientBackground',
        beforeDraw: (chart) => {
            const { ctx, chartArea } = chart;
            if (!chartArea) return;

            const centerX = (chartArea.left + chartArea.right) / 2;
            const centerY = (chartArea.top + chartArea.bottom) / 2;

            const width = chartArea.right - chartArea.left;
            const height = chartArea.bottom - chartArea.top;

            const diagonal = Math.sqrt(width * width + height * height) / 2;
            const angle = (45 + 19) * (Math.PI / 180);

            const startX = centerX - diagonal * Math.cos(angle);
            const startY = centerY + diagonal * Math.sin(angle);
            const endX = centerX + diagonal * Math.cos(angle);
            const endY = centerY - diagonal * Math.sin(angle);

            const gradient = ctx.createLinearGradient(
                startX,
                startY,
                endX,
                endY
            );

            gradient.addColorStop(0, '#4CAF50');
            gradient.addColorStop(0.3, '#8BC34A');
            gradient.addColorStop(0.5, '#FFEB3B');
            gradient.addColorStop(0.7, '#FF9800');
            gradient.addColorStop(1, '#F44336');

            ctx.save();
            ctx.fillStyle = gradient;
            ctx.fillRect(chartArea.left, chartArea.top, chartArea.right - chartArea.left, chartArea.bottom - chartArea.top);
            ctx.restore();
        }
    };

    const externalTooltipHandler = (context) => {
        let tooltipEl = document.getElementById('chartjs-tooltip-risk-matrix');

        if (!tooltipEl) {
            tooltipEl = document.createElement('div');
            tooltipEl.id = 'chartjs-tooltip-risk-matrix';
            tooltipEl.innerHTML = '<div class="chartjs-tooltip-body-scrollable"></div>';
            document.body.appendChild(tooltipEl);
        }

        const tooltipModel = context.tooltip;
        if (tooltipModel.opacity === 0) {
            tooltipEl.style.opacity = 0;
            tooltipEl.style.pointerEvents = 'none';
            return;
        }

        const position = context.chart.canvas.getBoundingClientRect();

        function getBody(bodyItem) {
            return bodyItem.lines;
        }

        if (tooltipModel.body) {
            const bodyLines = tooltipModel.body.map(getBody);

            let innerHtml = '';
            bodyLines.forEach((body, i) => {
                if (i > 0) {
                    innerHtml += '<div class="tooltip-event-separator"></div>';
                }
                if (Array.isArray(body)) {
                    body.forEach((line) => {
                        innerHtml += '<div>' + line + '</div>';
                    });
                } else {
                    innerHtml += '<div>' + body + '</div>';
                }
            });

            const bodyEl = tooltipEl.querySelector('.chartjs-tooltip-body-scrollable');
            if (bodyEl) {
                bodyEl.innerHTML = innerHtml;
            }
        }

        tooltipEl.style.opacity = 1;
        tooltipEl.style.position = 'absolute';
        tooltipEl.style.left = position.left + window.pageXOffset + tooltipModel.caretX + 10 + 'px';
        tooltipEl.style.top = position.top + window.pageYOffset + tooltipModel.caretY + 10 + 'px';
        tooltipEl.style.pointerEvents = 'none';
        tooltipEl.style.background = 'rgba(0, 0, 0, 0.8)';
        tooltipEl.style.borderRadius = '6px';
        tooltipEl.style.padding = '10px';
        tooltipEl.style.color = '#fff';
        tooltipEl.style.fontSize = '12px';
        tooltipEl.style.maxWidth = '300px';
        tooltipEl.style.zIndex = '10000';
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
            intersect: false,
            mode: 'point'
        },
        onClick: (event) => {
            const xScale = event.chart.scales.x;
            const yScale = event.chart.scales.y;
            const xValue = xScale.getValueForPixel(event.x);
            const yValue = yScale.getValueForPixel(event.y);

            const urlParams = new URLSearchParams(location.search);
            const queryStartDate = urlParams.get('startDate');
            const queryEndDate = urlParams.get('endDate');

            const hasQueryRange = Boolean(queryStartDate && queryEndDate) && moment(queryStartDate).isValid() && moment(queryEndDate).isValid();
            const startDate = hasQueryRange ? moment.utc(queryStartDate).startOf('day').toISOString() : moment.utc().startOf('month').toISOString();
            const endDate = hasQueryRange ? moment.utc(queryEndDate).endOf('day').toISOString() : moment.utc().endOf('month').toISOString();

            dispatch(getHeatmapZoneStats({
                startDate,
                endDate,
                minSeverity: Math.max(0, yValue - 10),
                maxSeverity: Math.min(100, yValue + 10),
                minEvents: Math.max(0, xValue - 5),
                maxEvents: Math.min(100, xValue + 5)
            }));

            setClickPosition({ x: event.native.clientX, y: event.native.clientY });
            setShowZoneInfo(true);
        },
        plugins: {
            legend: {
                display: false
            },
            tooltip: {
                enabled: false,
                external: externalTooltipHandler,
                callbacks: {
                    title: () => '',
                    label: (context) => {
                        const point = context.raw;
                        return [
                            `${messages?.['driver_performance_driver_detail_riskMatrix_driverName'] || 'Driver Name'}: ${point.driverName || 'N/A'}`,
                            `${messages?.['driver_performance_driver_detail_riskMatrix_riskScore'] || 'Risk Score'}: ${point.riskScore || 'N/A'}`,
                            `${messages?.['driver_performance_driver_detail_riskMatrix_avgSeverity'] || 'Avg Severity'}: ${point.avgSeverity || point.y}`,
                            `${messages?.['driver_performance_driver_detail_riskMatrix_avgEventCount'] || 'Normalized Avg Events'}: ${point.normalizedAvgEvents || point.x}`
                        ];
                    }
                }
            }
        },
        scales: {
            x: {
                type: 'linear',
                position: 'bottom',
                min: 0,
                max: 100,
                title: {
                    display: false
                },
                ticks: {
                    display: false
                },
                grid: {
                    display: false
                }
            },
            y: {
                type: 'linear',
                min: 0,
                max: 100,
                title: {
                    display: false
                },
                ticks: {
                    display: false
                },
                grid: {
                    display: false
                }
            }
        }
    };


    return (
        <div style={{ position: 'relative', width: '230px', height: '150px' }}>
            <Scatter
                data={chartData}
                options={chartOptions}
                plugins={[gradientBackgroundPlugin]}
            />
            <div style={{ marginTop: '-10px', marginLeft: '11px', fontSize: '13px' }}>
                {messages['driver_performance_driver_detail_risk'] || 'Risk'}
            </div>
            {showZoneInfo && zoneStats && (
                <div style={{
                    position: 'fixed', left: clickPosition.x + 10, top: clickPosition.y + 10,
                    background: 'white', border: '2px solid #333', borderRadius: '8px',
                    padding: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    minWidth: '250px', zIndex: 10000, fontSize: '12px'
                }}>
                    <div style={{ fontWeight: 'bold', marginBottom: '8px', fontSize: '14px' }}>Zone Statistics</div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid #f5f5f5' }}>
                        <span>Driver Count:</span><strong>{zoneStats.driverCount}</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid #f5f5f5' }}>
                        <span>Risk Score:</span><strong>{zoneStats.avgRiskScore?.toFixed(1)}</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid #f5f5f5' }}>
                        <span>Average Severity:</span><strong>{zoneStats.avgSeverity?.toFixed(1)}</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid #f5f5f5' }}>
                        <span>Avg Risk Event Count:</span><strong>{zoneStats.avgEventCount?.toFixed(1)}</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
                        <span>Avg Distance Traveled:</span><strong>{zoneStats.avgDistance?.toFixed(2)} km</strong>
                    </div>
                </div>
            )}
        </div>
    );
};

const DriverDetail = () => {

    const { messages } = useIntl()

    //Hooks
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    // Params
    const { id } = useParams();

    const loading = useSelector(state => state.driverPerformanceDriverViewRouteRedux.loading);
    const driver = useSelector(state => state.driverPerformanceDriverViewRouteRedux.driver);
    const error = useSelector(state => state.driverPerformanceDriverViewRouteRedux.error);
    const driversSummary = useSelector(state => state.driverPerformanceDriverViewRouteRedux.driversSummary);
    const authenticatedId = useSelector(state => state.securityRedux.authenticatedId);
    const user = useSelector(state => state.securityRedux.user);
    const isKph = user?.isKph;

    const [checked, setChecked] = useState(false);

    const [dateRange, setDateRange] = useState({
        startDate: null,
        endDate: null
    });

    const [queryDateRange, setQueryDateRange] = useState({
        startDate: null,
        endDate: null
    });

    const getReturnTabFromSource = (source) => {
        switch (source) {
            case 'eventsDriverInfo':
            case 'eventsCaseInfo':
                return 'events';
            case 'dashboardCaseInfo':
                return 'dashboard';
            case 'cases':
            case 'casesCaseInfo':
                return 'cases';
            case 'drivers':
            case 'driverDetail':
            default:
                return 'drivers';
        }
    };

    const getReturnTabLabel = (tabValue) => {
        switch (tabValue) {
            case 'dashboard':
                return messages?.['driver_performance_dashboard'] || 'Dashboard';
            case 'events':
                return messages?.['driver_performance_events'] || 'Events';
            case 'cases':
                return messages?.['driver_performance_cases'] || 'Cases';
            case 'drivers':
            default:
                return messages?.['driver_performance_driver_moduleDrivers'] || 'Drivers';
        }
    };

    const getReturnLink = () => {
        const urlParams = new URLSearchParams(location.search);
        const sourceModule = urlParams.get('sourceModule') || 'driverDetail';

        const returnTab = urlParams.get('returnTab') || getReturnTabFromSource(sourceModule);
        return {
            tab: returnTab,
            link: `/app/driverPerformance/main?tab=${returnTab}`
        };
    };

    const getDateRangePillLabel = () => {
        const urlParams = new URLSearchParams(location.search);
        const sourceModule = urlParams.get('sourceModule') || 'driverDetail';

        const momentFormat = GetFormatForMoment(user?.dateformat) || 'MM/DD/YYYY';

        const startValue = queryDateRange?.startDate || dateRange?.startDate;
        const endValue = queryDateRange?.endDate || dateRange?.endDate;

        // Drivers tab intentionally displays UTC day bounds (matches its query construction).
        // Events/Cases/Dashboard should display in local time to match their pills.
        const shouldDisplayUtc = sourceModule === 'driversTab' || sourceModule === 'drivers';

        const startLabel = startValue
            ? (shouldDisplayUtc ? moment.utc(startValue) : moment(startValue)).format(`${momentFormat} hh:mm a`)
            : '';
        const endLabel = endValue
            ? (shouldDisplayUtc ? moment.utc(endValue) : moment(endValue)).format(`${momentFormat} hh:mm a`)
            : '';

        const timeFrameLabel = messages?.['driver_performance_events_custom'] || 'Custom';
        return `${timeFrameLabel} (${startLabel} - ${endLabel})`;
    };

    useEffect(() => {
        const urlParams = new URLSearchParams(location.search);
        const queryStartDate = urlParams.get('startDate');
        const queryEndDate = urlParams.get('endDate');
        const sourceModule = urlParams.get('sourceModule');

        if (!id) return;

        const hasQueryRange =
            Boolean(queryStartDate && queryEndDate) &&
            moment(queryStartDate).isValid() &&
            moment(queryEndDate).isValid();

        if (hasQueryRange) {
            setQueryDateRange({ startDate: queryStartDate, endDate: queryEndDate });
        } else {
            setQueryDateRange({ startDate: null, endDate: null });
        }

        // Use ISO strings with milliseconds to match other modules (e.g. .000Z / .999Z)
        const startDate = hasQueryRange
            ? moment.utc(queryStartDate).startOf('day').toISOString()
            : moment.utc().startOf('month').toISOString();

        const endDate = hasQueryRange
            ? moment.utc(queryEndDate).endOf('day').toISOString()
            : moment.utc().endOf('month').toISOString();

        setDateRange({ startDate, endDate });

        dispatch(getDriverPerformanceDriver({
            driverId: id,
            startDate,
            endDate,
            sourceModule: sourceModule || 'driverDetail'
        }));

        dispatch(getDriversSummary({
            driverId: parseInt(id),
            startDate,
            endDate
        }));

        dispatch(getRiskByDriver({ driverId: id, startDate: startDate, endDate: endDate }));
        dispatch(getRiskEventDistribution({ driverId: id, startDate: startDate, endDate: endDate }));

    }, [id, location.search, dispatch])

    const handleChange = () => {
        const preferenceValue = { showDetails: !checked };
        setChecked(!checked);
        dispatch(savePreference({ entityName: "User", entityId: authenticatedId, preferenceKey: OVERVIEW_KEY, preferenceValue: preferenceValue }));
    };

    const getTabs = () => {
        let tabs = [
            {
                label: messages['driver_performance_driver_overview'],
                render: () => {
                    return <OverviewTabDriver driverId={id} />
                }
            },
        ]
        return tabs
    }

    return (
        <Container className="container-view">
            {loading && <LinearProgress />}
            <Paper elevation={0} className="paper-white" >
                <Grid
                    container
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    style={{ padding: "15px 20px 0 20px" }}
                >
                    <Grid item xs>
                        <div style={{ display: 'flex', alignItems: 'center', width: '100%', gap: 10 }}>
                            <div style={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                                <Breadcrumb
                                    links={[
                                        {
                                            title: getReturnTabLabel(getReturnLink().tab),
                                            link: getReturnLink().link,
                                        },
                                        {
                                            title: driver?.driverName || messages['driver_performance_driver_title'],
                                        },
                                    ]}
                                    navigate={navigate}
                                />
                            </div>

                            <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', minWidth: 0 }}>
                                {
                                    Boolean(dateRange?.startDate && dateRange?.endDate) && (
                                        <Pill
                                            textField={"label"}
                                            item={{ id: 'dateRange', label: getDateRangePillLabel(), color: '#005bf8' }}
                                            variant='filled'
                                            onClick={function noRefCheck() { }}
                                        />
                                    )
                                }
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <IconButton
                                    icon={checked ? "expand_less" : "expand_more"}
                                    tooltip={checked ? messages['driver_performance_driver_detail_hideDescription'] : messages['driver_performance_driver_detail_showDescription']}
                                    onClick={() => handleChange()}
                                />
                            </div>
                        </div>
                    </Grid>
                </Grid>
                {
                    loading ? (
                        <Skeleton
                            variant="rectangular"
                            animation="wave"
                            height='60px'
                            style={{ marginLeft: 10, marginRight: 10 }}
                        />
                    ) : (
                        <div style={{ marginLeft: 10, marginRight: 10 }}>
                            <Preferences
                                preferencesKey={OVERVIEW_KEY}
                                onInitialPreferences={(data) => {
                                    if (typeof data?.showDetails === "boolean") {
                                        setChecked(data?.showDetails);
                                    }
                                }}
                            >
                                <Collapse in={checked}>
                                    <div className="driverPerformance-details-subheader">
                                        <div className="driverPerformance-details-subheader-text">
                                            <div>
                                                <span className="driverPerformance-details-subheader-title">
                                                    {messages['driver_performance_driver_name']}:
                                                </span>
                                                <span className="driverPerformance-details-subheader-content">
                                                    {driver?.driverName || 'N/A'}
                                                </span>
                                            </div>
                                            {
                                                driver?.supervisorName &&
                                                <div>
                                                    <span className="driverPerformance-details-subheader-title">
                                                        {messages['driver_performance_driver_detail_supervisorName']}:
                                                    </span>
                                                    <span className="driverPerformance-details-subheader-content">
                                                        {driver?.supervisorName || 'N/A'}
                                                    </span>
                                                </div>
                                            }
                                            <div>
                                                <span className="driverPerformance-details-subheader-title">
                                                    {messages['driver_performance_driver_totalDistance']}:
                                                </span>
                                                <span className="driverPerformance-details-subheader-content">
                                                    {`${Math.round(!isKph ? driver?.totalDistance || 0 : (driver?.totalDistance || 0) * 1.60934)} ${isKph ? messages?.['kilometers'] || 'km' : messages?.['miles'] || 'mi'}`}
                                                </span>
                                            </div>
                                        </div>
                                        <div style={{ marginTop: '20px' }}>
                                            <RiskMatrixChart driversSummaryData={driversSummary} />
                                        </div>
                                    </div>
                                </Collapse>
                            </Preferences>
                            <Tabs id="driverDetailTabs" scrollButtons>
                                {
                                    getTabs().map((tab, index) => {
                                        return (
                                            <TabContainer key={index} label={tab.label} index={index}>
                                                {tab.render()}
                                            </TabContainer>
                                        )
                                    })
                                }
                            </Tabs>
                        </div>
                    )
                }
            </Paper>
        </Container>
    )
}

export default DriverDetail
